import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { SeriesItem, VodItem } from "@hybrid-iptv/types";
import { api, runsOnDevice } from "../api/client.js";
import { nativeCatalogAvailable } from "../api/nativeCatalog.js";
import type { DiscoveryPeople } from "../api/nativeCatalog.js";
import { providerBody } from "../epg/api.js";
import { normalizeTitle } from "../home/catalogTitleMatch.js";
import { hash } from "../home/shelfFill.js";
import type { TitleCastMember } from "./TitleDetail.js";

/**
 * Cast photos from Wikimedia Commons (roadmap Section 26 step 7; ADR amendment 2026-10-11).
 * The people files are split by name, so the page fetches only the slices its provider's
 * cast names fall in (/api/discovery/people/<n>.json) and matches the names; the device's
 * list of the title's own Wikidata people settles two people of one name. Every photo shown
 * is credited on About's Image credits screen.
 */

/** As the job writes them (scripts/discovery/lib.mjs PEOPLE_SHARDS, shardOf): by the person's name. */
export const PEOPLE_SHARDS = 256;
export const shardOf = (name: string) => hash(normalizeTitle(name)) % PEOPLE_SHARDS;

/** One person in a people file: [name, thumb, photographer, licence, licence URL, Commons file]. */
export type PersonPhoto = [string, string, string, string, string, string];
export type PeopleShard = Record<string, PersonPhoto>;

/** The site calls its own server; the APK calls ours by name (youtubeApi.ts's pattern). */
const SERVER = "https://sliquetv.online";

export function peopleShardUrl(shard: number): string {
  const path = `/api/discovery/people/${shard}.json`;
  return runsOnDevice() ? SERVER + path : path;
}

export async function fetchPeopleShard(shard: number): Promise<PeopleShard> {
  const response = await fetch(peopleShardUrl(shard));
  if (response.status === 404) return {};
  if (!response.ok) throw new Error(`people ${response.status}`);
  return (await response.json()) as PeopleShard;
}

/** A Commons thumbnail's whole address, from the path the file keeps. */
export function photoUrl(thumb: string): string {
  return `https://upload.wikimedia.org/wikipedia/commons/thumb/${thumb}`;
}

/** The Commons page of a photo: its photographer, licence and history. */
export function commonsPage(file: string): string {
  return `https://commons.wikimedia.org/wiki/File:${encodeURIComponent(file)}`;
}

/**
 * The provider's cast with photos where a person's name matches. `people` comes in order of
 * preference (the title's own Wikidata people first), so two people of one name resolve to
 * the one in this title.
 */
export function withCastPhotos(cast: TitleCastMember[] | undefined, people: PersonPhoto[]): TitleCastMember[] | undefined {
  if (!cast || people.length === 0) return cast;
  const byName = new Map<string, PersonPhoto>();
  for (const person of people) {
    const key = normalizeTitle(person[0]);
    if (key && !byName.has(key)) byName.set(key, person);
  }
  return cast.map((member) => {
    if (member.profile_path || member.photoUrl) return member;
    const person = byName.get(normalizeTitle(member.name));
    return person ? { ...member, photoUrl: photoUrl(person[1]) } : member;
  });
}

const DAY = 24 * 60 * 60 * 1000;

/** The open title's cast, with Commons photos added under Open on the device. */
export function useCastPhotos<T extends VodItem | SeriesItem>(
  sourceId: string,
  kind: "vod" | "series",
  item: T | null,
  cast: TitleCastMember[] | undefined,
  enabled: boolean,
): TitleCastMember[] | undefined {
  // On the device the native catalog names the title's own people (they only break ties); the
  // website has no such answer, and the photos do not wait for it.
  const device = runsOnDevice() && nativeCatalogAvailable();
  const on = enabled && item !== null && (device || !runsOnDevice()) && (cast?.length ?? 0) > 0;
  const people = useQuery({
    queryKey: ["discoveryPeople", sourceId, kind, item?.id],
    queryFn: () =>
      api.post<DiscoveryPeople | null>(`/api/sources/${encodeURIComponent(sourceId)}/discovery/people`, {
        ...providerBody(),
        request: { kind, itemId: item!.id },
      }),
    enabled: on && device && Boolean(sourceId),
    staleTime: DAY,
    retry: false,
  });
  // The title's own people only break ties; the photos do not wait for them.
  const ids = useMemo(() => new Set([...(people.data?.cast ?? []), ...(people.data?.people ?? [])]), [people.data]);
  const names = useMemo(() => (on ? (cast ?? []).map((member) => member.name) : []), [on, cast]);
  const shards = useMemo(() => [...new Set(names.map(shardOf))].sort((a, b) => a - b), [names]);
  const files = useQueries({
    queries: shards.map((shard) => ({
      queryKey: ["peopleShard", shard],
      queryFn: () => fetchPeopleShard(shard),
      staleTime: DAY,
      gcTime: DAY,
      retry: 1,
    })),
  });
  const stamp = files.map((file) => file.dataUpdatedAt).join(",");
  return useMemo(() => {
    if (!on) return cast;
    const own: PersonPhoto[] = [];
    const others: PersonPhoto[] = [];
    files.forEach((file) => {
      for (const [id, person] of Object.entries(file.data ?? {})) (ids.has(Number(id)) ? own : others).push(person);
    });
    return withCastPhotos(cast, [...own, ...others]);
    // stamp stands in for files, a new array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, cast, ids, stamp]);
}
